import { AIChatBot } from '@ecomm/ai-chat/AIChatBot'
import { AIChatExperimentWrapper } from '@ecomm/ai-chat/AIChatExperimentWrapper'
import { ALBY_CHAT_BOT_ID } from '@ecomm/ai-chat/constants'
import { useLocation } from '@ecomm/framework/useLocation'
import { InitializeStore, PXRudderstackObserver } from '@ecomm/gatsby-wrappers'
import { NinetailedPromotionWrapper } from '@ecomm/promotions-components/NinetailedPromotionWrapper'
import { BrazeExperimentDataWrapper } from '@ecomm/shared-ninetailed'
import {
  ReferrerUrlContext,
  useReferrerUrl
} from '@ecomm/tracking/TrackingProvider/useReferrerUrl'
import { useSetAwinCookie } from '@ecomm/tracking/awin'
import type { Locale } from '@ecomm/utils/locale/types'
import { type ReactElement, StrictMode, Suspense } from 'react'

import { useExcludeAIChatForRoute } from '@ecomm/ai-chat/useExcludeAIChatForRoute'
import { useIsAIChatExperiment } from '@ecomm/ai-chat/useIsAIChatExperiment'
import { LiveChat } from '@ecomm/live-chat'
import { ModalProvider } from '@ecomm/modal/lib/ModalContext/ModalProvider'
import { SkipToA11yStatementLink } from '@ecomm/shared-components/lib/SkipToA11yStatementLink'
import { SkipToContentLink } from '@ecomm/shared-components/lib/SkipToContentLink'
import { useRef } from 'react'
import { Prefetch } from './Prefetch'

export type PageContext = {
  readonly locale: Locale
  readonly enableJotai?: boolean
}

type Props = {
  readonly element: ReactElement
  readonly props: {
    readonly pageContext: PageContext
  }
}
function WrapWithContext<P extends Props>({ element, props: p }: P) {
  const { locale, enableJotai } = p.pageContext
  const location = useLocation()
  const referrerUrl = useReferrerUrl(location)

  useSetAwinCookie(locale)

  const experimentRef = useRef<HTMLDivElement>(null)
  const {
    isVariant,
    status,
    enabled: isChatEnabled
  } = useIsAIChatExperiment({
    ref: experimentRef
  })
  const isExcludedRoute = useExcludeAIChatForRoute()
  const showAIChatWidget = isChatEnabled && !isExcludedRoute

  return (
    // StrictMode is inserted here instead of WrapRootElement because it needs
    // to be below @reach/router's BrowserRouter to avoid hydration issues
    // since Gatsby's using an outdated version of @reach/router.
    // https://stackoverflow.com/questions/71832720/link-tag-inside-browserrouter-changes-only-the-url-but-doesnt-render-the-compo/71833424#71833424
    <StrictMode>
      <InitializeStore enableJotai={!!enableJotai}>
        <ReferrerUrlContext.Provider
          value={{ ...referrerUrl, referrerUrl: referrerUrl.referrerUrl || '' }}
        >
          <ModalProvider>
            <BrazeExperimentDataWrapper>
              <PXRudderstackObserver />
              <NinetailedPromotionWrapper locale={locale} />
              <Prefetch />
              <SkipToContentLink />
              <SkipToA11yStatementLink />
              {/* Suspense is added here as a fallback. */}
              <Suspense>{element}</Suspense>
              <LiveChat />
              <AIChatExperimentWrapper
                enabled={showAIChatWidget}
                experimentRef={experimentRef}
                isVariant={isVariant}
                status={status}
              >
                <AIChatBot widgetId={ALBY_CHAT_BOT_ID} />
              </AIChatExperimentWrapper>
              <div id="modals" />
            </BrazeExperimentDataWrapper>
          </ModalProvider>
        </ReferrerUrlContext.Provider>
      </InitializeStore>
    </StrictMode>
  )
}

// Wrap the wrapper in another wrapper to allow the use of React hooks
// https://github.com/gatsbyjs/gatsby/issues/22833#issuecomment-609370401
export function WrapPageElement<P extends Props>(props: P) {
  return <WrapWithContext {...props} />
}
